import {
  useAppointmentProposalSurveyOptions,
  useAuthenticatedUser,
  useOrganization,
  useSystemTypes,
} from '@airship-platform/airship-patterns';
import {
  useOrganizationSOWs,
  useProducts,
  useProposalOptionNameDefaults,
} from '@airship-platform/airship-proposal';
import { EmptyState, Loader } from '@airship-platform/jam-web/components';
import { useEffect } from 'react';
import { Outlet, useParams } from 'react-router';
import { useAppointmentProposalFiles } from '../../data-access/appointmentProposalFiles';
import { useAppointmentProposalOptionModifiers } from '../../data-access/appointmentProposalOptionModifiers';
import { useAppointmentProposalOptionProducts } from '../../data-access/appointmentProposalOptionProducts';
import { useAppointmentProposalOptions } from '../../data-access/appointmentProposalOptions';
import { useAppointmentProposalOptionSOWs } from '../../data-access/appointmentProposalOptionSows';
import { useAppointmentProposals } from '../../data-access/appointmentProposals';
import { useAppointmentProposalUpgrades } from '../../data-access/appointmentProposalUpgrades';
import { useAppointments } from '../../data-access/appointments';
import { useFinancingConfig } from '../../data-access/financingConfig';
import { useJobImagesProvider } from '../../data-access/jobImages';
import { usePricebookUniversalUpgradeProducts } from '../../data-access/pricebook-universal-upgrades-product';
import { useProposalIntegrations } from '../../data-access/proposalIntegrations';
import { useProposalTemplates } from '../../data-access/proposalTemplate';
import { useAppointmentProposalCosts } from '../../data-offline';
import { OfflineErrorAlert } from '../offline-diagnostics';
import { useProposalScore } from './hooks/useProposalScore';

const ProposalPage: React.FC = () => {
  const { appointmentId, appointmentProposalId, appointmentProposalOptionId } =
    useParams<{
      appointmentId: string;
      appointmentProposalId: string;
      appointmentProposalOptionId: string;
    }>();

  const {
    isLoading: orgIsLoading,
    isLoaded: orgIsLoaded,
    fetchOrganization,
  } = useOrganization();

  const {
    currentAppointment,
    isLoading: apptIsLoading,
    isLoaded: apptIsLoaded,
  } = useAppointments();

  const {
    currentProposal,
    errorKind: proposalErrorKind,
    isLoading: proIsLoading,
    isLoaded: proIsLoaded,
    fetchAppointmentProposal,
  } = useAppointmentProposals();

  const {
    currentOption,
    isLoading: apoIsLoading,
    isLoaded: apoIsLoaded,
    clearSelectedOption,
    fetchAppointmentProposalOption,
    fetchAppointmentProposalOptions,
  } = useAppointmentProposalOptions();

  const { fetchAppointmentProposalUpgradesProducts } =
    useAppointmentProposalUpgrades();
  const { fetchAppointmentProposalProducts } =
    useAppointmentProposalOptionProducts();
  const { fetchAppointmentProposalModifiers } =
    useAppointmentProposalOptionModifiers();
  const { fetchAppointmentProposalSOWs } = useAppointmentProposalOptionSOWs();

  const { fetchProposalBuilderData } = useAppointmentProposalSurveyOptions();
  const { fetchSystemTypes } = useSystemTypes();

  const {
    fetchOptionNameData,
    isLoaded: isOptionNamesLoaded,
    isLoading: isOptionNamesLoading,
  } = useProposalOptionNameDefaults();

  const { fetchPricebookUniversalUpgradeProducts } =
    usePricebookUniversalUpgradeProducts();

  const { fetchFinancingConfigs } = useFinancingConfig();
  const { fetchAppointmentFiles } = useAppointmentProposalFiles();
  const { fetchJobImages } = useJobImagesProvider();
  const {
    queryProposalTemplateOptions,
    queryProposalTemplates,
    fetchProposalTemplateTags,
  } = useProposalTemplates();
  const { updateProductsFilter } = useProducts();
  const { fetchSOWs } = useOrganizationSOWs();
  const { fetchProposalIntegrations } = useProposalIntegrations();
  const { user } = useAuthenticatedUser();
  const { triggerCostsCalculation } = useAppointmentProposalCosts();

  useProposalScore();

  useEffect(() => {
    const abortController = new AbortController();

    fetchSystemTypes();
    fetchAppointmentFiles();
    fetchJobImages();
    fetchFinancingConfigs();
    fetchPricebookUniversalUpgradeProducts();
    fetchProposalTemplateTags({ signal: abortController.signal });

    return () => abortController.abort('ProposalPage');
  }, [
    fetchAppointmentFiles,
    fetchJobImages,
    fetchFinancingConfigs,
    fetchSystemTypes,
    fetchProposalTemplateTags,
    fetchPricebookUniversalUpgradeProducts,
  ]);

  useEffect(() => {
    if (appointmentId && appointmentProposalId) {
      const abortController = new AbortController();
      fetchAppointmentProposal(appointmentId, appointmentProposalId, {
        signal: abortController.signal,
      });
      fetchAppointmentProposalOptions(appointmentId, appointmentProposalId, {
        signal: abortController.signal,
      });
      fetchAppointmentProposalUpgradesProducts(
        appointmentId,
        appointmentProposalId
      );
      fetchAppointmentProposalProducts(appointmentId, appointmentProposalId);
      fetchAppointmentProposalModifiers(appointmentId, appointmentProposalId);
      fetchAppointmentProposalSOWs(appointmentId, appointmentProposalId);

      return () => abortController.abort('ProposalPage');
    }
  }, [
    appointmentId,
    appointmentProposalId,
    fetchAppointmentProposal,
    fetchAppointmentProposalModifiers,
    fetchAppointmentProposalOptions,
    fetchAppointmentProposalUpgradesProducts,
    fetchAppointmentProposalProducts,
    fetchAppointmentProposalSOWs,
  ]);

  useEffect(() => {
    if (appointmentId && appointmentProposalId && appointmentProposalOptionId) {
      const abortController = new AbortController();
      fetchAppointmentProposalOption(
        appointmentId,
        appointmentProposalId,
        appointmentProposalOptionId,
        { signal: abortController.signal }
      );
      triggerCostsCalculation(appointmentProposalOptionId);

      return () => abortController.abort('ProposalPage');
    }
  }, [
    appointmentId,
    appointmentProposalId,
    appointmentProposalOptionId,
    fetchAppointmentProposalOption,
    triggerCostsCalculation,
  ]);

  useEffect(() => {
    if (appointmentProposalOptionId) {
      return;
    }

    if (
      currentOption?.appointmentProposalId &&
      currentOption.appointmentProposalId !== appointmentProposalId
    ) {
      clearSelectedOption();
    }
  }, [
    appointmentProposalId,
    appointmentProposalOptionId,
    currentOption?.appointmentProposalId,
    clearSelectedOption,
  ]);

  useEffect(() => {
    if (user?.organizationId) {
      fetchProposalBuilderData(user.organizationId);
      fetchOptionNameData(user.organizationId);
      fetchOrganization(user.organizationId);
      fetchSOWs(user.organizationId);
      fetchProposalIntegrations(user.organizationId);
    }
  }, [
    fetchProposalBuilderData,
    fetchOptionNameData,
    fetchSOWs,
    fetchOrganization,
    fetchProposalIntegrations,
    user?.organizationId,
  ]);

  useEffect(() => {
    const businessUnitId = currentAppointment?.job.businessUnitId || null;
    updateProductsFilter(
      { businessUnitIds: businessUnitId ? [businessUnitId] : [] },
      { reset: true }
    );

    if (!businessUnitId) {
      return;
    }

    const abortController = new AbortController();

    queryProposalTemplateOptions(
      { businessUnitIds: [businessUnitId] },
      { signal: abortController.signal }
    );
    queryProposalTemplates(
      { businessUnitIds: [businessUnitId] },
      { signal: abortController.signal }
    );

    return () => abortController.abort('ProposalPage');
  }, [
    currentAppointment?.job.businessUnitId,
    updateProductsFilter,
    queryProposalTemplateOptions,
    queryProposalTemplates,
  ]);

  if (!currentAppointment) {
    return (
      <EmptyState
        title="Appointment not found"
        subtitle="Oops! The requested appointment was not found."
      />
    );
  }

  if (proIsLoaded && proposalErrorKind === 'NOT_FOUND') {
    return (
      <EmptyState
        title="Proposal not found"
        subtitle="Oops! The requested proposal was not found."
      />
    );
  }

  const isLoaded =
    orgIsLoaded &&
    proIsLoaded &&
    apptIsLoaded &&
    apoIsLoaded &&
    isOptionNamesLoaded;

  const isLoading =
    orgIsLoading ||
    proIsLoading ||
    apptIsLoading ||
    apoIsLoading ||
    isOptionNamesLoading;

  const isProposalRouteMatch = !!(
    currentProposal &&
    currentProposal.appointmentProposalId === appointmentProposalId
  );

  if ((isLoading && !isLoaded) || !isProposalRouteMatch) {
    return <Loader isLoading message="Loading Proposal" />;
  }

  return (
    <>
      <OfflineErrorAlert />
      <Outlet />
    </>
  );
};

export default ProposalPage;
